import { useEffect, useMemo, useRef, useState } from "react";
import "./TravelRouteAnimation.css";

const defaultDestinations = [
  {
    id: "start",
    name: "Vapi",
    description: "Your journey begins",
    x: 54,
    y: 282,
  },
  {
    id: "mumbai",
    name: "Mumbai",
    description: "City connections",
    x: 174,
    y: 168,
  },
  {
    id: "surat",
    name: "Surat",
    description: "A smooth midway stop",
    x: 318,
    y: 240,
  },
  {
    id: "vadodara",
    name: "Vadodara",
    description: "Culture and comfort",
    x: 452,
    y: 116,
  },
  {
    id: "ahmedabad",
    name: "Ahmedabad",
    description: "The road opens up",
    x: 600,
    y: 188,
  },
  {
    id: "udaipur",
    name: "Udaipur",
    description: "Arrive inspired",
    x: 714,
    y: 70,
  },
];

function createRoutePath(destinations) {
  return destinations.reduce((path, destination, index) => {
    if (index === 0) return `M ${destination.x} ${destination.y}`;
    const previous = destinations[index - 1];
    const midpoint = (previous.x + destination.x) / 2;
    const bend = index % 2 === 0 ? 34 : -34;
    return `${path} C ${midpoint} ${previous.y + bend}, ${midpoint} ${destination.y - bend}, ${destination.x} ${destination.y}`;
  }, "");
}

function calculateUprightCarTransform(point, nextPoint) {
  const dx = nextPoint.x - point.x;
  const dy = nextPoint.y - point.y;
  const movingLeft = dx < 0;
  const rawSlope = (Math.atan2(dy, Math.abs(dx)) * 180) / Math.PI;
  const tilt = Math.max(-22, Math.min(22, rawSlope));
  const scaleX = movingLeft ? -1 : 1;
  const rotation = movingLeft ? -tilt : tilt;

  return {
    transformSvg: `translate(${point.x} ${point.y}) scale(${scaleX}, 1) rotate(${rotation})`,
    transformCss: `translate(-50%, -50%) scaleX(${scaleX}) rotate(${rotation}deg)`,
  };
}

export default function TravelRouteAnimation({
  destinations = defaultDestinations,
  autoPlay = true,
  scrollDriven = false,
  showLabels = true,
  pageMode = false,
  viewBox = "0 0 760 360",
  duration = 11000,
  routeColor = "var(--gold-400)",
  sectionColors = [],
  vehicle = "car",
}) {
  const rootRef = useRef(null);
  const pathRef = useRef(null);
  const progressRef = useRef(null);
  const carRef = useRef(null);
  const pageCarRef = useRef(null);
  const pinRefs = useRef([]);
  const frameRef = useRef(0);
  const startTimeRef = useRef(0);
  const [visible, setVisible] = useState(false);

  const routePath = useMemo(
    () => createRoutePath(destinations),
    [destinations],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: pageMode ? 0.01 : 0.18 },
    );
    if (rootRef.current) observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const path = pathRef.current;
    const progressPath = progressRef.current;
    const car = carRef.current;
    const pageCar = pageCarRef.current;
    if (!path || !progressPath || (!car && !pageCar) || !destinations.length)
      return undefined;
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const totalLength = path.getTotalLength();
    path.style.strokeDasharray = `${totalLength}`;
    progressPath.style.strokeDasharray = `${totalLength}`;
    if (!progressPath.style.strokeDashoffset)
      progressPath.style.strokeDashoffset = `${totalLength}`;

    function setProgress(progress) {
      const clamped = Math.min(1, Math.max(0, progress));
      if (sectionColors.length) {
        const colorIndex = Math.min(
          sectionColors.length - 1,
          Math.floor(clamped * sectionColors.length),
        );
        rootRef.current?.style.setProperty(
          "--route-color",
          sectionColors[colorIndex],
        );
      }
      progressPath.style.strokeDashoffset = `${totalLength * (1 - clamped)}`;
      const point = path.getPointAtLength(totalLength * clamped);
      const nextPoint = path.getPointAtLength(
        Math.min(totalLength, totalLength * clamped + 4),
      );
      const { transformSvg, transformCss } = calculateUprightCarTransform(
        point,
        nextPoint,
      );
      car?.setAttribute("transform", transformSvg);
      if (pageCar) {
        const [, , viewBoxWidth, viewBoxHeight] = viewBox
          .split(" ")
          .map(Number);
        pageCar.style.left = `${(point.x / viewBoxWidth) * 100}%`;
        pageCar.style.top = `${(point.y / viewBoxHeight) * 100}%`;
        pageCar.style.transform = transformCss;
      }
      destinations.forEach((destination, index) => {
        const reached = clamped >= index / Math.max(1, destinations.length - 1);
        pinRefs.current[index]?.classList.toggle("is-reached", reached);
      });
    }

    if (reducedMotion || !autoPlay) {
      setProgress(reducedMotion ? 1 : 0);
      return undefined;
    }

    if (!visible && !pageMode && !scrollDriven) {
      cancelAnimationFrame(frameRef.current);
      return undefined;
    }

    if (scrollDriven) {
      let pageHeight = typeof window !== 'undefined' ? window.innerHeight * 2 : 1600;
      let scheduled = false;

      const updateHeight = () => {
        requestAnimationFrame(() => {
          if (rootRef.current?.parentElement) {
            pageHeight = rootRef.current.parentElement.offsetHeight || (window.innerHeight * 2);
          }
        });
      };

      const updateFromScroll = () => {
        if (!scheduled) {
          scheduled = true;
          requestAnimationFrame(() => {
            scheduled = false;
            const scrollRange = pageMode
              ? Math.max(1, pageHeight - window.innerHeight)
              : Math.max(window.innerHeight * 1.15, pageHeight);
            const progress = Math.min(1, Math.max(0, window.scrollY / scrollRange));
            setProgress(progress);
          });
        }
      };

      requestAnimationFrame(() => {
        updateHeight();
        updateFromScroll();
      });
      window.addEventListener("resize", updateHeight, { passive: true });
      window.addEventListener("scroll", updateFromScroll, { passive: true });
      return () => {
        window.removeEventListener("resize", updateHeight);
        window.removeEventListener("scroll", updateFromScroll);
      };
    }

    startTimeRef.current = performance.now();
    const animate = (now) => {
      const elapsed = (now - startTimeRef.current) % duration;
      setProgress(elapsed / duration);
      frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [
    autoPlay,
    destinations,
    duration,
    pageMode,
    routePath,
    scrollDriven,
    sectionColors,
    visible,
  ]);

  return (
    <div
      ref={rootRef}
      className={`travel-route${pageMode ? " travel-route--page" : ""}`}
      style={{ "--route-color": routeColor }}
      role="img"
      aria-label={
        showLabels
          ? `Animated travel route from ${destinations[0]?.name || "start"} to ${destinations[destinations.length - 1]?.name || "destination"}`
          : "Animated travel route with multiple destinations"
      }
    >
      <svg
        viewBox={viewBox}
        preserveAspectRatio={pageMode ? "none" : "xMidYMid meet"}
        aria-hidden="true"
      >
        <defs>
          <filter id="routeGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="routeGradient" x1="0" x2="1">
            <stop offset="0" stopColor="var(--gold-300)" />
            <stop offset="1" stopColor="#61d0ad" />
          </linearGradient>
        </defs>
        <path ref={pathRef} className="travel-route__base" d={routePath} />
        <path
          ref={progressRef}
          className="travel-route__progress"
          d={routePath}
        />
        {!pageMode &&
          destinations.map((destination, index) => (
            <g
              key={destination.id}
              ref={(element) => {
                pinRefs.current[index] = element;
              }}
              className="travel-route__destination"
              transform={`translate(${destination.x} ${destination.y})`}
            >
              <circle className="travel-route__halo" r="22" />
              <path
                className="travel-route__pin"
                d="M0-22c-10 0-17 8-17 17 0 12 17 28 17 28S17 7 17-5C17-14 10-22 0-22zm0 23a6 6 0 110-12 6 6 0 010 12z"
              />
              {showLabels && (
                <>
                  <text className="travel-route__label" x="24" y="-7">
                    {destination.name}
                  </text>
                  <text className="travel-route__description" x="24" y="10">
                    {destination.description}
                  </text>
                </>
              )}
            </g>
          ))}
        {!pageMode && (
          <g
            ref={carRef}
            className={`travel-route__vehicle travel-route__vehicle--${vehicle}`}
            filter="url(#routeGlow)"
          >
            <path
              d={
                vehicle === "van"
                  ? "M-27 7h54M-22 7l4-17h28l12 17M-13 7a4 4 0 108 0m18 0a4 4 0 108 0"
                  : "M-24 7h48M-18 7l5-13h26l9 13M-12 7a4 4 0 108 0m14 0a4 4 0 108 0"
              }
            />
            <path
              className="travel-route__vehicle-window"
              d={vehicle === "van" ? "M-14-8h21l7 8h-31z" : "M-9-6h18l5 7h-28z"}
            />
          </g>
        )}
      </svg>
      {pageMode && (
        <div className="travel-route__page-layer">
          {destinations.map((destination, index) => (
            <span
              key={destination.id}
              ref={(element) => {
                pinRefs.current[index] = element;
              }}
              className="travel-route__page-pin"
              style={{
                left: `${(destination.x / 760) * 100}%`,
                top: `${(destination.y / 1600) * 100}%`,
              }}
            >
              <span />
            </span>
          ))}
          <span
            ref={pageCarRef}
            className={`travel-route__page-car travel-route__page-car--${vehicle}`}
          >
            <svg viewBox="0 0 48 24" aria-hidden="true">
              <path
                d="M3.5 13.8L7.6 8.6C8.5 7.5 9.8 6.8 11.2 6.8H31.5C32.9 6.8 34.2 7.5 35 8.5L39.2 13.2C41.2 14 42.5 15 42.5 16.8V18C42.5 18.7 41.9 19.3 41.2 19.3H39C38.2 20.8 36.6 21.8 34.7 21.8C32.8 21.8 31.2 20.8 30.4 19.3H21.6C20.8 20.8 19.2 21.8 17.3 21.8C15.4 21.8 13.8 20.8 13 19.3H6.8C6.1 19.3 5.5 18.7 5.5 18V16.7C5.5 15.4 6 14.3 7 13.8Z"
                fill="none"
                stroke="#040A16"
                strokeWidth="2.8"
                strokeLinejoin="round"
              />
              <path
                d="M3.5 13.8L7.6 8.6C8.5 7.5 9.8 6.8 11.2 6.8H31.5C32.9 6.8 34.2 7.5 35 8.5L39.2 13.2C41.2 14 42.5 15 42.5 16.8V18C42.5 18.7 41.9 19.3 41.2 19.3H39C38.2 20.8 36.6 21.8 34.7 21.8C32.8 21.8 31.2 20.8 30.4 19.3H21.6C20.8 20.8 19.2 21.8 17.3 21.8C15.4 21.8 13.8 20.8 13 19.3H6.8C6.1 19.3 5.5 18.7 5.5 18V16.7C5.5 15.4 6 14.3 7 13.8ZM17.3 20.3C18.4 20.3 19.3 19.4 19.3 18.3C19.3 17.2 18.4 16.3 17.3 16.3C16.2 16.3 15.3 17.2 15.3 18.3C15.3 19.4 16.2 20.3 17.3 20.3ZM34.7 20.3C35.8 20.3 36.7 19.4 36.7 18.3C36.7 17.2 35.8 16.3 34.7 16.3C33.6 16.3 32.7 17.2 32.7 18.3C32.7 19.4 33.6 20.3 34.7 20.3ZM12.6 8.3C12.1 8.3 11.6 8.6 11.4 9.1L8.7 12.5H23.6V8.3H12.6ZM25.1 8.3V12.5H38.1L35.1 9.2C34.7 8.7 34.2 8.3 33.6 8.3H25.1Z"
                fill="#F3CF6B"
              />
              <polygon points="11.8,8.8 9.3,12 23.6,12 23.6,8.8" fill="#FFFFFF" opacity="0.95" />
              <polygon points="25.1,8.8 25.1,12 37.4,12 34.8,8.8" fill="#FFFFFF" opacity="0.95" />
              <circle cx="17.3" cy="18.3" r="2.2" fill="#040A16" />
              <circle cx="34.7" cy="18.3" r="2.2" fill="#040A16" />
              <circle cx="17.3" cy="18.3" r="0.8" fill="#F3CF6B" />
              <circle cx="34.7" cy="18.3" r="0.8" fill="#F3CF6B" />
            </svg>
          </span>
        </div>
      )}
    </div>
  );
}
